import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

const STACK = {
  Frontend: ["HTML", "CSS", "JavaScript", "React", "Next.js"],
  Backend: ["Node.js", "Express", "PHP"],
  Database: ["PostgreSQL", "MongoDB"],
  "CMS / E-commerce": ["WordPress", "WooCommerce"],
  Mobile: ["Flutter", "Android", "iOS"],
  "Cloud / Infra": ["AWS", "Cloud Hosting", "REST APIs"],
  AI: ["AI APIs", "Generative AI", "Automation"],
};

const FILTERS = ["All", ...Object.keys(STACK)];

export const TechStack = () => {
  const [active, setActive] = useState("All");
  const groups = active === "All" ? Object.entries(STACK) : [[active, STACK[active]]];

  return (
    <section id="technology" className="bg-mist py-24 sm:py-32" data-testid="technology-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Technology</Eyebrow>
          <h2 className="mt-5 font-jakarta text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl" data-testid="technology-heading">
            Technology We Work With
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-9 flex flex-wrap gap-2" role="tablist" aria-label="Technology categories">
            {FILTERS.map((f) => (
              <button
                key={f}
                role="tab"
                aria-selected={active === f}
                onClick={() => setActive(f)}
                data-testid={`tech-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`rounded-full px-4 py-2 font-jakarta text-[12.5px] font-semibold transition-colors duration-300 ${
                  active === f ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-gold hover:text-gold"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 space-y-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              className="space-y-8"
            >
              {groups.map(([group, items]) => (
                <div key={group} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`tech-group-${group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="font-mono2 text-[10px] font-semibold uppercase tracking-[0.28em] text-gold">{group}</p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    {items.map((t, i) => (
                      <motion.span
                        key={t}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.05 }}
                        className="cursor-default rounded-lg border border-slate-200 bg-mist px-4 py-2 font-jakarta text-[13px] font-semibold text-navy transition-[border-color,background-color,color] duration-300 hover:border-navy hover:bg-navy hover:text-gold-light"
                      >
                        {t}
                      </motion.span>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
